import React, { useEffect, useState, useCallback, useRef } from "react";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ZoomModal from "./heatmap/zoomModal";
import { dimensionParticipation } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import { API_RESPONSE } from "../utills/app-constant";
import Modal from "../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";
import Remark from "../admin/components/Charts/Remark";

const DimensionDetails = ({ selectedCompanyId, selectedCampaignId, type, graphName, title = "Dimension Participation" }) => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({ series: [], options: {} });
  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState("")
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);

  const modalId = `zoomModal-${title.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dimensionParticipation(selectedCompanyId, selectedCampaignId, type, graphName);
      if (response?.data?.responseCode === 200) {
        const data = response.data.responseBody || [];
        setRemarkText(response.data.remark || "");
        setRemarkId(response.data.remarkId || "");
        setRowData(data);
        const names = data.map(d => String(d.dimensionName));
        const counts = data.map(d => Number(d.count) || 0);
        const maxCount = Math.max(...counts, 0);
        const tickAmount = Math.ceil(maxCount / 5) || 5;

        setChartData({
          series: [{ name: "Participants", data: counts }],
          options: {
            chart: { id: "dimension_bar", type: "bar", toolbar: { show: false } },
            plotOptions: {
              bar: { horizontal: true, barHeight: "30%", dataLabels: { position: "right" } },
            },
            dataLabels: {
              enabled: true, formatter: val => val, style: { fontSize: "12px", colors: ["#000"] }, offsetX: 10,           // ✅ push label outside the bar to the right
              textAnchor: "start",
            },
            xaxis: { categories: names },
            yaxis: { min: 0, max: Math.ceil(maxCount / 10) * 10, tickAmount },
            colors: ["#4D76A1"],
            tooltip: { y: { formatter: val => `${val} participants` } },
          },
        });

      } else if (response?.data?.responseCode === 404) {
        setRowData([]);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Export
  const exportAsExcel = () => {
    const headers = ["Dimension Name", "Count"];
    const rows = rowData.map(d => [d.dimensionName, d.count]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${title}.xlsx`);
  };

 const exportAsPDF = async () => {
  const chartBox = document.getElementById(`chart-box-${title}`);
  if (!chartBox) return;
  chartBox.scrollTo(0, 0);
  await new Promise(r => setTimeout(r, 200));
 
  const chartElement = chartBox.querySelector(".apexcharts-canvas");
  if (!chartElement) return;
 
  const canvas = await html2canvas(chartElement, {
    scale: 2,
    backgroundColor: "#fff",
    useCORS: true,
  });
 
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("l", "mm", "a4");
 
  const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
  const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF
 
  const canvasWidth = canvas.width; // Width of the canvas image
  const canvasHeight = canvas.height; // Height of the canvas image
 
  // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
  const scaleFactor = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%
 
  const imgWidth = canvasWidth * scaleFactor; // Scaled width
  const imgHeight = canvasHeight * scaleFactor; // Scaled height
 
  // Calculate the offset to center the image
  const xOffset = (pdfWidth - imgWidth) / 2;
  const yOffset = (pdfHeight - imgHeight) / 2;
 
  // Add the image to the PDF, centered and scaled down
  pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
 
  // Save the PDF
  pdf.save(`${title || "chart"}.pdf`);
};
 
const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };
 const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div id={`chart-box-${title}`} className="card border h-100 ">
      <div className="d-flex justify-content-between align-items-center overall-text mb-2 p-2">
        <h5>{title}</h5>
        <div className="d-flex align-items-center">
          <button className="border-0 bg-transparent me-2" data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
            {/* <i className="bi bi-zoom-in"></i> */}
            <img src={ZOOM_ICON} alt="zoom" />

          </button>
          <div className="dropdown">
            <button className="icon-btn bg-transparent" type="button" data-bs-toggle="dropdown">
              {/* <i className="bi bi-three-dots"></i> */}
              <img src={MENU_ICON} alt="menu" className="" />

            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={exportAsExcel}>Download Excel</button></li>
              <li><button className="dropdown-item" onClick={exportAsPDF}>Export PDF</button></li>
              <li><button className="dropdown-item" onClick={exportAsImage}>Download Image</button></li>
            </ul>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loader-container"><div className="loader"></div></div>
      ) : (
        <Chart options={chartData.options} series={chartData.series} type="bar" height={300} />
      )}
      <div className="d-flex justify-content-end my-3">
        <button
          title="Add Comment"
          onClick={() => {
            setShowRemarkModal(true);
            setTimeout(() => {
              if (modalRef.current) modalRef.current.click();
            }, 0);
          }}
          className="border-0 bg-transparent position-absolute"
          style={{
            bottom: "10px",
            right: "10px",

          }}
        >
          {/* <i className="bi bi-chat-dots"></i> */}
          <img src={CHAT_ICON} alt="chat" />

        </button>
      </div>

      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Remark" buttonLable="">
          <Remark
            selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remarkText}
            remarkId={remarkId}
            isAttempt={0}
            setRemarkprops={setRemarkText}
            setRemarkIdProps={setRemarkId}

            onCloseModal={() => setShowRemarkModal(false)}
          />
        </Modal>
      )}
      <ZoomModal
        id={modalId}
        title={title}
        chartOptions={chartData.options}
        seriesData={chartData.series}
        height={400}
        type="bar"
        remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
      />
    </div>
  );
};

export default DimensionDetails;
